import React, { useContext } from 'react';
import { Box, Button, IconButton, Rating, Typography, styled } from '@mui/material';
import { SelectedSkillContext } from 'data/index';
import ImageComponent from 'ui/components/shared/ImageComponent';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useTranslation } from 'react-i18next';
import WithBookSkillData from 'ui/containers/skillary/BookSkillContainer';
import { BookingCardPropType } from 'entities/interfaces';
import { useNavigate } from 'react-router-dom';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import StarIcon from '@mui/icons-material/Star';

const MobileWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.common.white,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}));

const MobileHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '1rem',
  backgroundColor: theme.palette.common.white,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  position: 'sticky',
  top: 0,
  zIndex: 10,
  height: '4rem',
  boxSizing: 'border-box'
}));

const MobileContent = styled(Box)({
  flex: 1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column'
});

const ImageContainer = styled(Box)({
  width: '100%',
  height: '250px',
  position: 'relative'
});

const InfoSection = styled(Box)({
  padding: '1.5rem 1rem',
  backgroundColor: '#424242',
  color: '#ffffff',
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const TitleContainer = styled(Box)({
  marginBottom: '1rem'
});

const CourseTitle = styled(Typography)({
  fontSize: '1.5rem',
  fontWeight: 700,
  lineHeight: 1.3,
  marginBottom: '0.5rem',
  color: '#ffffff'
});

const CourseSubtitle = styled(Typography)({
  fontSize: '1rem',
  opacity: 0.8,
  marginBottom: '1rem',
  color: '#ffffff'
});

const StatsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '1.5rem',
  flexWrap: 'wrap',
  gap: '1rem'
});

const StatItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.3rem'
});

const RatingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
});

const DescriptionContainer = styled(Box)({
  marginBottom: '1.5rem'
});

const DescriptionTitle = styled(Typography)({
  fontSize: '1.1rem',
  fontWeight: 600,
  marginBottom: '0.8rem',
  color: 'white'
});

const DescriptionText = styled(Typography)({
  fontSize: '0.95rem',
  lineHeight: 1.5,
  opacity: 0.9,
  color: 'white'
});

const ChaptersContainer = styled(Box)({
  flex: 1
});

const ChaptersTitle = styled(Typography)({
  fontSize: '1.1rem',
  fontWeight: 600,
  marginBottom: '0.8rem',
  color: 'white'
});

const ChapterItem = styled(Typography)({
  fontSize: '0.95rem',
  lineHeight: 1.6,
  opacity: 0.9,
  marginBottom: '0.3rem',
  color: 'white'
});

const ButtonContainer = styled(Box)({
  padding: '1rem',
  backgroundColor: '#424242',
  display: 'flex',
  gap: '0.8rem'
});

const ActionButton = styled(Button)({
  flex: 1,
  height: '3rem',
  borderRadius: '0.5rem',
  fontWeight: 600,
  fontSize: '0.95rem'
});

const MobileBookSkillCard = ({ bookCourse }: BookingCardPropType) => {
  const { t } = useTranslation();
  const { state: selectedSkill, updateContext: setSelectedSkill } = useContext(SelectedSkillContext);
  const navigate = useNavigate();

const handleBooking = async () => {
  if (selectedSkill) {
    try {
      console.log(`Buchung startet für Kurs-ID: ${selectedSkill.id}`);
      const resp = await bookCourse(selectedSkill.id);

      if (resp.status === 200) {
        console.log(`Buchung erfolgreich! Speichere Kurs-ID: ${selectedSkill.id}`);
        localStorage.setItem("lastBookedCourse", String(selectedSkill.id));
        
        // MySkills localStorage aktualisieren
        const mySkills = JSON.parse(localStorage.getItem("mySkills") || "[]");
        if (!mySkills.includes(selectedSkill.id)) {
          const updatedSkills = [...mySkills, selectedSkill.id];
          localStorage.setItem("mySkills", JSON.stringify(updatedSkills));
        }
        
        // Direkt zum Chat navigieren
        navigate(`/course/${selectedSkill.id}/chat`);
        setSelectedSkill(undefined);
      } else {
        console.error("Fehler beim Buchen:", resp);
      }
    } catch (err) {
      console.error("Netzwerk-/Serverfehler beim Buchen:", err);
    }
  }
};

  const handleBookLater = async () => {
  if (selectedSkill) {
    try {
      console.log(`Speichere Kurs für später: ${selectedSkill.id}`);
      const resp = await bookCourse(selectedSkill.id);
      if (resp.status === 200) {
        console.log(`Kurs-ID ${selectedSkill.id} erfolgreich gespeichert!`);
        
        // MySkills localStorage aktualisieren
        const mySkills = JSON.parse(localStorage.getItem("mySkills") || "[]");
        if (!mySkills.includes(selectedSkill.id)) {
          const updatedSkills = [...mySkills, selectedSkill.id];
          localStorage.setItem("mySkills", JSON.stringify(updatedSkills));
        }
      } else {
        console.error("Fehler beim Speichern des Kurses für später:", resp);
      }
    } catch (err) {
      console.error("Netzwerk-/Serverfehler beim Speichern:", err);
    }
  }
  setSelectedSkill(undefined);
};

  const handleBack = () => {
    setSelectedSkill(undefined);
  };

  if (!selectedSkill) return null;

  const isComingSoon = Number(selectedSkill.status) === 2;

  return (
    <MobileWrapper>
      <MobileHeader>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon fontSize="large" />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          {t('Course Details')}
        </Typography>
      </MobileHeader>
      <MobileContent>
        <ImageContainer>
          <ImageComponent
            src={selectedSkill.image}
            alt={selectedSkill.title}
            width="100%"
            height="100%"
            objectFit
          />
        </ImageContainer>
        <InfoSection>
          <TitleContainer>
            <CourseTitle>{selectedSkill.title}</CourseTitle>
            <CourseSubtitle>{selectedSkill.subtitle}</CourseSubtitle>
          </TitleContainer>

          <StatsContainer>
            <RatingContainer>
              <Rating
                value={selectedSkill.ratings || 0}
                precision={0.1}
                size="small"
                readOnly
                sx={{ 
                  '& .MuiRating-iconFilled': { color: '#ffd700' },
                  '& .MuiRating-iconEmpty': { color: '#666' }
                }}
              />
              <Typography fontWeight={700} fontSize="0.95rem" color="#ffffff">
                {selectedSkill.ratings ? selectedSkill.ratings.toFixed(1) : "–"}
              </Typography>
              <Typography fontSize="0.85rem" sx={{ opacity: 0.8, color: '#ffffff' }}>
                ({selectedSkill.ratingsCount ? selectedSkill.ratingsCount.toLocaleString().replace(',', '.') : "0"})
              </Typography>
            </RatingContainer>

            <Box sx={{ display: 'flex', gap: '1rem' }}>
              {typeof selectedSkill.duration === 'number' && selectedSkill.duration > 0 && (
                <StatItem>
                  <HourglassBottomIcon sx={{ fontSize: '1.1rem', opacity: 0.8, color: '#ffffff' }} />
                  <Typography fontSize="0.9rem" fontWeight={600} color="#ffffff">
                    {selectedSkill.duration} min
                  </Typography>
                </StatItem>
              )}

              {selectedSkill.coursePoints !== undefined && selectedSkill.coursePoints > 0 && (
                <StatItem>
                  <StarIcon sx={{ fontSize: '1.1rem', color: '#ffd700' }} />
                  <Typography fontSize="0.9rem" fontWeight={600} color="#ffffff">
                    {selectedSkill.coursePoints}
                  </Typography>
                </StatItem>
              )}
            </Box>
          </StatsContainer>

          <DescriptionContainer>
            <DescriptionTitle>{t('Description')}:</DescriptionTitle>
            <DescriptionText>
              {decodeURIComponent(selectedSkill.description)}
            </DescriptionText>
          </DescriptionContainer>

          <ChaptersContainer>
            <ChaptersTitle>{t('Chapter overview')}:</ChaptersTitle>
            {selectedSkill.chapters.map((name, index) => (
              <ChapterItem key={index}>
                {index + 1}. {name}
              </ChapterItem>
            ))}
          </ChaptersContainer>
        </InfoSection>
      </MobileContent>

      {!isComingSoon && (
        <ButtonContainer>
          <ActionButton
            variant="contained"
            color="primary"
            onClick={handleBooking}
            startIcon={<PlayArrowIcon />}
          >
            {t('Start Now')}
          </ActionButton>
          <ActionButton
            variant="outlined"
            onClick={handleBookLater}
            startIcon={<BookmarkBorderIcon />}
            sx={{
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {t('Later')}
          </ActionButton>
        </ButtonContainer>
      )}

      {isComingSoon && (
        <Box sx={{ 
          padding: "1rem", 
          textAlign: "center", 
          backgroundColor: '#424242'
        }}>
          <Typography fontSize="1rem" fontWeight={600} color="#ffffff">
            {t("This course will be available soon.")}
          </Typography>
        </Box>
      )}
    </MobileWrapper>
  );
};

export default WithBookSkillData(MobileBookSkillCard);